// panels/LogsPanel.jsx
import React, { useRef, useEffect, useState } from 'react';
import { useStore } from '../../../store/useStore';
import { PanelWrapper } from './PanelWrapper';
import LogDetailDrawer from '../../Logs/LogDetailDrawer';

const LEVEL_COLORS = {
  ERROR:    '#ef4444',
  CRITICAL: '#fca5a5',
  WARN:     '#fbbf24',
  WARNING:  '#fbbf24',
  INFO:     '#60a5fa',
  DEBUG:    '#64748b',
};

const SOURCE_COLORS = {
  GCP:      '#4285f4',
  AZURE:    '#0078d4',
  MONGODB:  '#4ade80',
  DATABASE: '#a78bfa',
};

function LogLine({ log, onClick }) {
  const levelColor  = LEVEL_COLORS[log.level]  || '#64748b';
  const sourceColor = SOURCE_COLORS[log.source] || '#64748b';
  const time = log.timestamp
    ? new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '--:--:--';

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', gap: 8, padding: '4px 10px',
        borderBottom: '1px solid rgba(255,255,255,0.03)',
        fontSize: 11, lineHeight: 1.5,
        alignItems: 'flex-start',
        cursor: 'pointer',
        transition: 'background .1s',
      }}
      className="hover:bg-white/[0.05]"
      title="Click to drill down"
    >
      <span className="mono" style={{ color: '#64748b', flexShrink: 0, fontSize: 10, paddingTop: 1 }}>{time}</span>
      <span style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.5px',
        color: sourceColor, background: `${sourceColor}18`,
        border: `1px solid ${sourceColor}30`,
        borderRadius: 3, padding: '1px 5px', flexShrink: 0, marginTop: 1,
      }}>
        {log.source}
      </span>
      <span style={{
        fontSize: 9, fontWeight: 700,
        color: levelColor, background: `${levelColor}18`,
        borderRadius: 3, padding: '1px 5px', flexShrink: 0, marginTop: 1,
      }}>
        {log.level}
      </span>
      <span style={{ color: 'var(--text-2)', flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
        {log.message}
      </span>
      {/* Drill-down chevron hint */}
      <span style={{ color: 'var(--text-3)', fontSize: 9, flexShrink: 0, marginTop: 2 }}>›</span>
    </div>
  );
}

export function LogsPanel({ maxRows = 50, loading }) {
  const { logs, variables } = useStore();
  const bottomRef = useRef(null);
  const [paused, setPaused]         = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const filtered = logs.filter(l => {
    if (variables?.source && variables.source !== 'ALL' && l.source !== variables.source) return false;
    if (variables?.severity && variables.severity !== 'ALL') {
      const sevMap = { CRITICAL: ['CRITICAL'], WARNING: ['WARN', 'WARNING'], INFO: ['INFO', 'DEBUG'] };
      if (!sevMap[variables.severity]?.includes(l.level)) return false;
    }
    return true;
  }).slice(0, maxRows);

  useEffect(() => {
    if (!paused) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs, paused]);

  function exportCsv() {
    const header = 'timestamp,source,level,service,message';
    const rows   = filtered.map(l =>
      [l.timestamp, l.source, l.level, l.service || '', JSON.stringify(l.message)].join(',')
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: 'logs.csv' });
    a.click();
  }

  return (
    <>
      <PanelWrapper
        title="Live Log Stream"
        subtitle={`${filtered.length} entries · click any row to drill down`}
        datasource="MongoDB"
        loading={loading}
        onExport={exportCsv}
      >
        {/* Terminal bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
          borderBottom: '1px solid var(--border)', background: 'var(--bg-tertiary)',
        }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {['#ef4444', '#f59e0b', '#22c55e'].map(c => (
              <div key={c} style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
            ))}
          </div>
          <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)', flex: 1 }}>
            stream://all-sources
          </span>
          <button
            onClick={() => setPaused(p => !p)}
            style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.5px',
              padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: paused ? 'rgba(245,158,11,.2)' : 'rgba(34,197,94,.15)',
              color: paused ? '#fbbf24' : '#22c55e',
            }}
          >
            {paused ? '⏸ PAUSED' : '● LIVE'}
          </button>
        </div>

        {/* Log rows */}
        <div style={{ height: 260, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
              <div className="mono">Waiting for logs…</div>
            </div>
          ) : (
            filtered.map((l, i) => (
              <LogLine
                key={l._id ? `${l._id}-${i}` : i}
                log={l}
                onClick={() => setSelectedLog(l)}
              />
            ))
          )}
          <div ref={bottomRef} />
        </div>
      </PanelWrapper>

      {selectedLog && (
        <LogDetailDrawer log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </>
  );
}
